/**
 * End Intake Button Component
 *
 * A polished button that validates required fields before ending an intake session.
 * Features:
 * - Immediate completion if all fields are filled
 * - Popover with missing fields list and 45-second countdown
 * - Animated countdown timer with progress ring
 * - Deep-links to navigate to missing fields
 * - "Mark Client Unavailable" option
 */

import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Loader2,
  PhoneOff,
  Square,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import {
  EndIntakeValidationResult,
  FirstContactOutcome,
  MissingField,
} from './types';
import { getScreenFromLabel } from './utils/endIntakeValidation';

const GRACE_PERIOD_SECONDS = 45;

interface EndIntakeButtonProps {
  validation: EndIntakeValidationResult;
  onEndIntake: (
    outcome: FirstContactOutcome,
    missingFields: MissingField[],
    unavailableReason?: string
  ) => void | Promise<void>;
  onNavigateToField: (fieldId: string, screen: 1 | 2) => void;
  disabled?: boolean;
  /** When true, shows the button in a subdued style (already clicked) but still clickable */
  hasBeenClicked?: boolean;
  className?: string;
  /** When true, programmatically opens the popover (e.g., after reject with missing fields) */
  autoOpen?: boolean;
  /** Called after the auto-open has been processed so parent can reset the flag */
  onAutoOpenHandled?: () => void;
  /** Whether the End Intake API call is in flight from the parent (e.g., auto-triggered after reject) */
  isEndingIntake?: boolean;
}

export const EndIntakeButton: React.FC<EndIntakeButtonProps> = ({
  validation,
  onEndIntake,
  onNavigateToField,
  disabled = false,
  hasBeenClicked = false,
  className,
  autoOpen = false,
  onAutoOpenHandled,
  isEndingIntake = false,
}) => {
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(GRACE_PERIOD_SECONDS);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [showUnavailableInput, setShowUnavailableInput] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const unavailableInputRef = useRef<HTMLTextAreaElement>(null);

  // Calculate progress for the ring (0 to 1)
  const progress = countdown / GRACE_PERIOD_SECONDS;

  // Clear interval on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Auto-open when triggered externally (e.g., after reject with missing fields)
  useEffect(() => {
    if (autoOpen) {
      setOpen(true);
      if (!validation.isValid) {
        startCountdown();
      }
      onAutoOpenHandled?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  // Start countdown when popover opens
  const startCountdown = useCallback(() => {
    setCountdown(GRACE_PERIOD_SECONDS);
    setIsCountdownActive(true);

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Stop countdown when popover closes
  const stopCountdown = useCallback(() => {
    setIsCountdownActive(false);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Handle countdown reaching zero - submit as incomplete
  const handleSubmitIncomplete = useCallback(async () => {
    stopCountdown();
    setIsSubmitting(true);
    try {
      await onEndIntake('incomplete', validation.missingFields);
    } finally {
      setIsSubmitting(false);
      setOpen(false);
    }
  }, [onEndIntake, validation.missingFields, stopCountdown]);

  // Effect to auto-submit when countdown reaches zero
  useEffect(() => {
    if (isCountdownActive && countdown === 0) {
      handleSubmitIncomplete();
    }
  }, [countdown, isCountdownActive, handleSubmitIncomplete]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      // Only handle closing here - opening is handled by handleClick
      // This prevents duplicate countdown starts from PopoverTrigger's internal handler
      if (!newOpen) {
        // Prevent closing during submission
        if (isSubmitting) return;
        setOpen(false);
        stopCountdown();
        setShowUnavailableInput(false);
        setUnavailableReason('');
      }
    },
    [stopCountdown, isSubmitting]
  );

  const handleClick = useCallback(() => {
    // Always open popover so user can choose outcome (including "Unavailable")
    setOpen(true);
    // Only start countdown if there are missing fields
    if (!validation.isValid) {
      startCountdown();
    }
  }, [validation.isValid, startCountdown]);

  const handleShowUnavailableInput = useCallback(() => {
    stopCountdown();
    setShowUnavailableInput(true);
    // Focus input after state update
    setTimeout(() => {
      unavailableInputRef.current?.focus();
    }, 100);
  }, [stopCountdown]);

  const handleSubmitUnavailable = useCallback(async () => {
    if (!unavailableReason.trim()) return;
    setIsSubmitting(true);
    try {
      await onEndIntake('unavailable', [], unavailableReason.trim());
    } finally {
      setIsSubmitting(false);
      setOpen(false);
      setShowUnavailableInput(false);
      setUnavailableReason('');
    }
  }, [onEndIntake, unavailableReason]);

  const handleBackFromUnavailable = useCallback(() => {
    setShowUnavailableInput(false);
    setUnavailableReason('');
    // Restart countdown if there are missing fields
    if (!validation.isValid) {
      startCountdown();
    }
  }, [validation.isValid, startCountdown]);

  const handleCompleteNow = useCallback(async () => {
    stopCountdown();
    setIsSubmitting(true);
    try {
      await onEndIntake('completed', []);
    } finally {
      setIsSubmitting(false);
      setOpen(false);
    }
  }, [onEndIntake, stopCountdown]);

  const handleFieldClick = useCallback(
    (field: MissingField) => {
      const screen = getScreenFromLabel(field.screen);
      stopCountdown();
      setOpen(false);
      onNavigateToField(field.fieldId, screen);
    },
    [onNavigateToField, stopCountdown]
  );

  // Revalidate and enable "Complete Now" if all fields are filled
  const canCompleteNow = validation.isValid;

  // SVG progress ring parameters
  const size = 40;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled || isSubmitting || isEndingIntake}
          className={cn(
            'h-9 px-4 rounded-full text-xs font-bold transition-all flex items-center gap-1.5',
            // Disabled state
            disabled && 'bg-zinc-800/60 text-zinc-600 cursor-not-allowed',
            // Submitting state (either from internal popover actions or parent-triggered)
            !disabled && (isSubmitting || isEndingIntake) && 'bg-white text-zinc-900 cursor-wait',
            // Already clicked state - subdued style like other buttons but still clickable
            !disabled && !isSubmitting && !isEndingIntake && hasBeenClicked && 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300 active:scale-95',
            // Not clicked yet - prominent (primary action)
            !disabled && !isSubmitting && !isEndingIntake && !hasBeenClicked && 'bg-white text-zinc-900 hover:bg-zinc-100 active:scale-95',
            className
          )}
        >
          {isSubmitting || isEndingIntake ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Square className="h-3.5 w-3.5" />
          )}
          {isSubmitting || isEndingIntake ? 'Ending...' : 'End Intake'}
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-80 p-0 bg-zinc-900 border-zinc-800 rounded-2xl overflow-hidden shadow-2xl"
        align="end"
        sideOffset={8}
      >
        {showUnavailableInput ? (
          /* Unavailable reason input view */
          <>
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
              <PhoneOff className="w-4 h-4 text-zinc-400" />
              <h4 className="text-xs font-black text-white uppercase tracking-wider">
                Client Unavailable
              </h4>
            </div>

            <div className="p-4 space-y-3">
              <p className="text-xs text-zinc-400">
                Why is the client unavailable?
              </p>
              <textarea
                ref={unavailableInputRef}
                value={unavailableReason}
                onChange={(e) => setUnavailableReason(e.target.value)}
                placeholder="e.g., No answer, voicemail full, wrong number..."
                className="w-full h-24 px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-sm text-white placeholder-zinc-500 resize-none focus:outline-none focus:ring-2 focus:ring-zinc-600 focus:border-transparent transition-all"
                disabled={isSubmitting}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && unavailableReason.trim()) {
                    e.preventDefault();
                    handleSubmitUnavailable();
                  }
                }}
              />
            </div>

            <div className="p-3 border-t border-zinc-800 space-y-2">
              <button
                type="button"
                onClick={handleSubmitUnavailable}
                disabled={!unavailableReason.trim() || isSubmitting}
                className={cn(
                  'w-full h-10 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2',
                  unavailableReason.trim() && !isSubmitting
                    ? 'bg-zinc-100 text-zinc-900 hover:bg-white active:scale-[0.98]'
                    : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                )}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <PhoneOff className="w-4 h-4" />
                )}
                {isSubmitting ? 'Ending Intake...' : 'Submit'}
              </button>
              <button
                type="button"
                onClick={handleBackFromUnavailable}
                disabled={isSubmitting}
                className="w-full h-10 rounded-xl bg-transparent text-zinc-500 text-xs font-bold hover:text-zinc-300 transition-all disabled:opacity-50"
              >
                Back
              </button>
            </div>
          </>
        ) : (
          /* Main end intake view */
          <>
            {/* Header - different based on validation state */}
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {canCompleteNow ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <h4 className="text-xs font-black text-white uppercase tracking-wider">
                      End Intake
                    </h4>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                    <h4 className="text-xs font-black text-white uppercase tracking-wider">
                      Missing Fields
                    </h4>
                  </>
                )}
              </div>

              {/* Countdown timer with progress ring - only show when there are missing fields */}
              {!canCompleteNow && isCountdownActive && (
                <div className="relative flex items-center justify-center">
                  <svg
                    width={size}
                    height={size}
                    className="transform -rotate-90"
                  >
                    {/* Background ring */}
                    <circle
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      fill="none"
                      stroke="rgb(39, 39, 42)"
                      strokeWidth={strokeWidth}
                    />
                    {/* Progress ring */}
                    <circle
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      fill="none"
                      stroke={countdown <= 10 ? 'rgb(239, 68, 68)' : 'rgb(251, 191, 36)'}
                      strokeWidth={strokeWidth}
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      className="transition-all duration-1000 ease-linear"
                    />
                  </svg>
                  {/* Timer text */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span
                      className={cn(
                        'text-[10px] font-black tabular-nums',
                        countdown <= 10 ? 'text-red-400' : 'text-amber-400'
                      )}
                    >
                      {countdown}s
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Missing fields list - only show when there are missing fields */}
            {!canCompleteNow && validation.missingFields.length > 0 && (
              <div className="max-h-48 overflow-y-auto">
                {validation.missingFields.map((field, index) => (
                  <button
                    key={`${field.fieldId}-${index}`}
                    type="button"
                    onClick={() => handleFieldClick(field)}
                    className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-zinc-800/50 transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider block">
                        {field.screen}
                      </span>
                      <span className="text-xs font-semibold text-white truncate block mt-0.5">
                        {field.label}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors flex-shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            )}

            {/* Action buttons */}
            <div className="p-3 border-t border-zinc-800 space-y-2">
              {/* Complete Now button - enabled only when validation passes */}
              <button
                type="button"
                onClick={handleCompleteNow}
                disabled={!canCompleteNow || isSubmitting}
                className={cn(
                  'w-full h-10 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2',
                  canCompleteNow && !isSubmitting
                    ? 'bg-emerald-500 text-white hover:bg-emerald-400 active:scale-[0.98]'
                    : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                )}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                {isSubmitting ? 'Ending Intake...' : canCompleteNow ? 'Complete' : 'Complete Fields Above'}
              </button>

              {/* Mark Client Unavailable */}
              <button
                type="button"
                onClick={handleShowUnavailableInput}
                disabled={isSubmitting}
                className={cn(
                  'w-full h-10 rounded-xl bg-zinc-800 text-zinc-400 text-xs font-bold hover:bg-zinc-700 hover:text-zinc-300 transition-all active:scale-[0.98] flex items-center justify-center gap-2',
                  isSubmitting && 'opacity-50 cursor-not-allowed'
                )}
              >
                <PhoneOff className="w-4 h-4" />
                Client Unavailable
              </button>

              {/* Auto-submit notice - only show when there are missing fields */}
              {!canCompleteNow && isCountdownActive && !isSubmitting && (
                <p className="text-[9px] text-zinc-400 text-center pt-1">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Auto-submits as incomplete when timer reaches 0
                </p>
              )}
              {isSubmitting && (
                <p className="text-[9px] text-zinc-400 text-center pt-1">
                  <Loader2 className="w-3 h-3 inline mr-1 animate-spin" />
                  Ending intake...
                </p>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
